import React, { useState } from 'react';
import { X, Clock } from 'lucide-react';

interface ScheduleMeetingModalProps {
  selectedDate: string;
  meetingDuration: number;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const ScheduleMeetingModal: React.FC<ScheduleMeetingModalProps> = ({
  selectedDate,
  meetingDuration,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    meetingTopic: ''
  });
  
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState<'Eastern' | 'Central' | 'Mountain' | 'Pacific'>('Eastern');
  
  const timezones = [
    { value: 'Eastern', label: 'ET' },
    { value: 'Central', label: 'CT' },
    { value: 'Mountain', label: 'MT' },
    { value: 'Pacific', label: 'PT' }
  ];
  
  // Generate time slots - 6 times throughout the day
  const getTimeSlots = () => {
    // Standard business hours: 9AM, 10AM, 12PM, 1PM, 3PM, 4PM
    const hours = [9, 10, 12, 13, 15, 16];
    return hours.map(hour => {
      const amPm = hour < 12 ? 'AM' : 'PM';
      const hourDisplay = hour % 12 || 12; // Convert 0 to 12 for display
      return `${hourDisplay}:00 ${amPm}`;
    });
  };
  
  const timeSlots = getTimeSlots();
  
  // Calculate meeting duration in minutes (rounded up)
  const durationInMinutes = Math.ceil((meetingDuration || 1800) / 60);
  
  const handleTimeSelect = (time: string) => {
    setSelectedTimes(prev => {
      // If already selected, remove it
      if (prev.includes(time)) {
        return prev.filter(t => t !== time);
      }
      
      // If less than 3 selected, add it
      if (prev.length < 3) {
        return [...prev, time];
      }
      
      // If 3 already selected, replace the oldest one (FIFO)
      const newTimes = [...prev];
      newTimes.shift(); // Remove the first item
      return [...newTimes, time];
    });
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get the double's user_id for contact creation
      const { data: doubleData, error: doubleError } = await supabase
        .from('doubles')
        .select('user_id')
        .eq('id', double.id)
        .single();

      if (doubleError) throw doubleError;

      // Create or update contact
      const { data: contact, error: contactError } = await supabase
        .from('contacts')
        .upsert({
          user_id: doubleData.user_id,
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          notes: formData.meetingTopic
        }, {
          onConflict: 'user_id,email',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (contactError) throw contactError;

      // Randomly select one of the suggested times
      const selectedTimeIndex = Math.floor(Math.random() * selectedTimes.length);
      const selectedTime = selectedTimes[selectedTimeIndex];

      // Create the meeting
      const startTime = new Date(`${selectedDate}T${selectedTime}`);
      const endTime = new Date(startTime.getTime() + (meetingDuration * 1000));

      const { data: meeting, error: meetingError } = await supabase
        .from('meetings')
        .insert({
          double_id: double.id,
          contact_id: contact.id,
          title: formData.meetingTopic,
          date: selectedDate,
          time: selectedTime,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          type: 'outbound',
          context: formData.meetingTopic,
          timezone: selectedTimezone
        })
        .select()
        .single();

      if (meetingError) throw meetingError;

      onSubmit({
        contact,
        meeting,
        selectedTime
      });
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      setError('Failed to schedule meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Format the selected date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl border-2 border-dark shadow-retro max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 relative">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Book your meeting</h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="mb-6">
            <p className="text-lg font-medium">
              {formatDate(selectedDate)}
            </p>
            <div className="flex items-center mt-2 text-gray-600">
              <Clock className="h-5 w-5 mr-2" />
              <span>{durationInMinutes} minute meeting</span>
            </div>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  className="w-full px-4 py-2 border-2 border-dark rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  className="w-full px-4 py-2 border-2 border-dark rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2 border-2 border-dark rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-2 border-2 border-dark rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Select up to 3 preferred times</h3>
              <p className="text-sm text-gray-600 mb-4">
                You've selected {selectedTimes.length} of 3 possible time slots
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {timeSlots.map((time, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleTimeSelect(time)}
                    className={`
                      py-3 px-4 rounded-lg text-center transition-all border-2
                      ${selectedTimes.includes(time) 
                        ? 'bg-primary text-white border-primary shadow-none -translate-y-1' 
                        : 'bg-white border-dark hover:bg-gray-50 shadow-retro hover:shadow-none hover:-translate-y-0.5'}
                    `}
                  >
                    {time}
                  </button>
                ))}
              </div>
              
              {selectedTimes.length === 0 && (
                <p className="text-red-500 text-sm mt-2">Please select at least one time slot</p>
              )}
            </div>
            
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Select a timezone for the call</h3>
              <div className="grid grid-cols-4 gap-2">
                {timezones.map((timezone) => (
                  <button
                    key={timezone.value}
                    type="button"
                    onClick={() => setSelectedTimezone(timezone.value as typeof selectedTimezone)}
                    className={`py-2 px-4 rounded-lg text-center transition-all border-2 ${
                      selectedTimezone === timezone.value
                        ? 'bg-primary text-white border-primary shadow-none -translate-y-1'
                        : 'bg-white border-dark hover:bg-gray-50 shadow-retro hover:shadow-none hover:-translate-y-0.5'
                    }`}
                  >
                    {timezone.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-6">
              <label htmlFor="meetingTopic" className="block text-sm font-medium text-gray-700 mb-1">
                What would you like to cover in the meeting? *
              </label>
              <textarea
                id="meetingTopic"
                name="meetingTopic"
                required
                rows={3}
                className="w-full px-4 py-2 border-2 border-dark rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                value={formData.meetingTopic}
                onChange={handleInputChange}
              />
            </div>
            
            
            <button
              type="submit"
              disabled={selectedTimes.length === 0 || isSubmitting}
              className={`w-full py-3 rounded-full transition-all border-2 border-dark font-bold
                ${selectedTimes.length > 0 && !isSubmitting
                  ? 'bg-primary hover:bg-primary/90 text-white shadow-retro hover:shadow-none hover:-translate-y-1' 
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'}
              `}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                  Scheduling...
                </div>
              ) : (
                'Suggest Times'
              )}
            </button>
            {error && (
              <p className="mt-2 text-sm text-red-600 text-center">{error}</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScheduleMeetingModal;